import { ModifyBudgetSkeleton } from '@/components/ModifyBudget';
import { useOptimizationSession } from '@/dashboards/CampaignOptimization/hooks/useOptimizationSession';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import { ChannelRow } from './ChannelRow';
import { FilteredChannels } from './FilteredChannels';
import { Footer } from './Footer';
import { Header } from './Header';
import { PeriodField } from './PeriodField';
import { TargetField } from './TargetField';

type Props = { onOutOfBoundsChannel?: (info: { channelName: string; min: number; max: number }) => void };

export const ControlPanel = ({ onOutOfBoundsChannel }: Props) => {
    const {
        draft, isDirty, isRunning, canRun, canEdit, isInitialLoading, isDataLoading,
        setDates, setBudget, resetDraft, runOptimization, disabledChannels, dateConstraints,
        data: { previous },
    } = useOptimizationSession();
    const { variableNameMap } = useDashboardGlobalContext();

    if (isInitialLoading || !draft || isDataLoading) {
        return (
            <div className="p-3 glass-tile">
                <ModifyBudgetSkeleton />
            </div>
        );
    }

    const categories = Object.entries(previous.roiCurves.variablesAndCategories.byCategory)
        .map(([category, variables]) => [category, variables.filter((v) => !disabledChannels.includes(v))] as const)
        .filter(([, variables]) => variables.length > 0);

    return (
        <div className="p-3 glass-tile">
            <Header isDirty={isDirty} onClick={resetDraft} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 px-6 py-4 bg-white/1">
                <PeriodField startDate={draft.startDate} endDate={draft.endDate} constraints={dateConstraints} disabled={!canEdit} onChange={setDates} />
                <div className="space-y-1">
                    <TargetField budget={draft.budget} onChange={setBudget} disabled={!canEdit} />
                </div>
            </div>
            <div className="px-3 ">
                {categories.map(([category, variables], index) => (
                    <div key={category}>
                        <div className="flex group">
                            <div className="w-8 flex items-center justify-center shrink-0 border-r border-white/5 group-hover:border-white/10 transition-colors">
                                <div className="text-nowrap text-[10px] font-bold tracking-widest uppercase text-white/40 -rotate-90">{category}</div>
                            </div>
                            <div className="grow space-y-1 py-1">
                                {variables.map((variable) => (
                                    <ChannelRow key={`${category}-${variable}`} variable={variable} onOutOfBoundsChannel={onOutOfBoundsChannel} />
                                ))}
                            </div>
                        </div>
                        {index < categories.length - 1 && <div className="h-px bg-linear-to-r from-transparent via-white/10 to-transparent my-2" />}
                    </div>
                ))}
            </div>
            <FilteredChannels channels={disabledChannels} variableNameMap={variableNameMap} />
            <Footer isDirty={isDirty} isRunning={isRunning} canRun={canRun} onClick={runOptimization} />
        </div>
    );
};
